import { z } from "zod";

export const capabilitySetSchema = z
  .object({
    casino_betting: z.boolean().default(false),
    platform_betting: z.boolean().default(false),
    packs_ownership: z.boolean().default(false),
    buyback: z.boolean().default(false),
    prize_pools: z.boolean().default(false),
  })
  .passthrough();

export const appearanceSchema = z
  .object({
    version: z.number().optional(),
    coat: z.string().optional(),
    mane: z.string().optional(),
    socks: z.array(z.unknown()).optional(),
  })
  .passthrough();

export const raceSummarySchema = z
  .object({
    raceId: z.string().uuid(),
    track: z.string(),
    trackName: z.string().optional(),
    scheduledAt: z.string(),
    bettingOpensAt: z.string().nullable().optional(),
    locksAt: z.string().nullable().optional(),
    status: z.string(),
    distanceM: z.number(),
    stage: z.string().optional(),
    weather: z.string().optional(),
    stageName: z.string().optional(),
    weatherName: z.string().optional(),
    fieldSize: z.number(),
    racecardHash: z.string().optional(),
    epoch: z.number().optional(),
    raceNumber: z.string().optional(),
    favourites: z
      .array(
        z.object({
          horseId: z.string().uuid(),
          name: z.string(),
          gate: z.number(),
          oddsH: z.number(),
          form: z.string().optional(),
          winRateBp: z.number().optional(),
          silk: z.string().optional(),
        }),
      )
      .optional(),
  })
  .passthrough();

export type RaceSummary = z.infer<typeof raceSummarySchema>;

export const racesSchema = z.object({
  races: z.array(raceSummarySchema),
});

export const raceResultSummarySchema = z.object({
  raceId: z.string().uuid(),
  track: z.string(),
  trackName: z.string().optional(),
  finishedAt: z.string().nullable(),
  distanceM: z.number(),
  order: z.array(z.number()),
  racecardHash: z.string(),
});

export const raceResultsSchema = z.object({
  races: z.array(raceResultSummarySchema),
  nextCursor: z.string().nullable().optional(),
});

export const scheduleSchema = z.object({
  from: z.string(),
  to: z.string(),
  tracks: z.array(
    z.object({
      slug: z.string(),
      name: z.string(),
    }),
  ),
  races: z.array(raceSummarySchema),
});

export type ScheduleData = z.infer<typeof scheduleSchema>;

export const horseSummarySchema = z
  .object({
    horseId: z.string().uuid(),
    name: z.string(),
    track: z.string(),
    trackName: z.string().optional(),
    tier: z.string(),
    starRating: z.string(),
    archetype: z.string(),
    bloodline: z.number(),
    appearance: appearanceSchema,
    isRetired: z.boolean(),
    popularityScore: z.number().optional(),
    nextRaceId: z.string().uuid().nullable().optional(),
    nextRaceAt: z.string().nullable().optional(),
    career: z.object({
      races: z.number(),
      wins: z.number(),
      places: z.number(),
      shows: z.number(),
      points: z.number(),
      form: z.string(),
      purseEarningsCents: z.number().optional(),
      prizeEarningsCents: z.number().optional(),
    }),
  })
  .passthrough();

export type HorseSummary = z.infer<typeof horseSummarySchema>;

export const horseRailSchema = z.object({
  horses: z.array(horseSummarySchema),
});

export const currentSeasonSchema = z.object({
  season: z.object({
    ordinal: z.number(),
    name: z.string(),
    status: z.string(),
    startsAt: z.string(),
    endsAt: z.string(),
    startEpoch: z.number().nullable().optional(),
    endEpoch: z.number().nullable().optional(),
  }),
});

export const runnerSchema = z
  .object({
    horseId: z.string().uuid(),
    gate: z.number(),
    name: z.string(),
    bloodline: z.number(),
    weight: z.number(),
    silk: z.string(),
    appearance: appearanceSchema,
    tier: z.string(),
    starRating: z.string(),
    archetype: z.string(),
    tack: z.record(z.string(), z.unknown()).optional(),
  })
  .passthrough();

export const racecardSchema = z
  .object({
    v: z.number(),
    raceId: z.string().uuid(),
    track: z.string(),
    trackName: z.string().optional(),
    scheduledAt: z.string(),
    distanceM: z.number(),
    lapDistanceM: z.number(),
    stage: z.string(),
    weather: z.string(),
    stageName: z.string().optional(),
    weatherName: z.string().optional(),
    rtpBp: z.number(),
    fieldSize: z.number(),
    epoch: z.number(),
    raceDurationMs: z.number(),
    runners: z.array(runnerSchema),
  })
  .passthrough();

export type Racecard = z.infer<typeof racecardSchema>;

export const racecardResponseSchema = z.object({
  racecard: racecardSchema,
  racecardHash: z.string(),
  signature: z.string(),
});

export const marketSchema = z.object({
  raceId: z.string().uuid(),
  status: z.string(),
  scheduledAt: z.string(),
  bettingOpensAt: z.string().nullable(),
  bettingOpen: z.boolean(),
  closedReason: z.string().nullable(),
  locksAt: z.string(),
  serverTime: z.string(),
  rtpBp: z.number(),
  board: z.record(z.string(), z.array(z.number())),
});

export const formGuideSchema = z.object({
  raceId: z.string().uuid(),
  tips: z.object({
    selections: z.array(z.number()),
    summary: z.string(),
  }),
  runners: z.array(
    z
      .object({
        horseId: z.string().uuid(),
        gate: z.number(),
        name: z.string(),
        form: z.string(),
        races: z.number(),
        wins: z.number(),
        places: z.number(),
        shows: z.number(),
        winRateBp: z.number(),
        recent: z.array(z.number().nullable()),
        comments: z.array(z.string()).default([]),
      })
      .passthrough(),
  ),
});

export const packHorseSchema = z
  .object({
    horseId: z.string().uuid(),
    name: z.string(),
    tier: z.string(),
    stickerCents: z.number(),
    phiAtAcquisition: z.number(),
    // The open window: a real, claimable delivery-day cash value and the
    // share to execute it against (absent on pre-window historical packs).
    shareId: z.number().optional(),
    instantQuoteCents: z.number().optional(),
    appearance: appearanceSchema,
    archetype: z.string(),
    starRating: z.string().optional(),
    bloodline: z.number().optional(),
  })
  .passthrough();

export type PackHorse = z.infer<typeof packHorseSchema>;

export const packPresentationBeatSchema = z.object({
  actualTier: z.string(),
  shownTier: z.string(),
  upgradeGap: z.number().int().nonnegative(),
  rainbow: z.boolean(),
  fakeFlipSchedule: z.array(z.boolean()),
  teaseCount: z.number().int().nonnegative(),
  sweatLevel: z.number().min(0).max(1),
});

export const packPurchaseSchema = z.object({
  positionId: z.string().uuid(),
  series: z.string(),
  seriesKind: z.string().optional(),
  trackName: z.string().optional(),
  status: z.string(),
  priceCents: z.number(),
  contents: z.array(packHorseSchema),
  deliveredAt: z.string().nullable(),
  presentationPlan: z
    .object({
      v: z.number(),
      order: z.array(z.number()),
      featuredIndex: z.number().nullable(),
      hitHorseId: z.string().uuid().nullable().optional(),
      beats: z.record(z.string(), packPresentationBeatSchema).optional(),
    })
    .optional(),
});

export type PackPurchase = z.infer<typeof packPurchaseSchema>;

export const packPurchaseBatchSchema = z.object({
  batchId: z.string().uuid(),
  clientRef: z.string().min(1),
  status: z.enum(["pending", "delivering", "partial", "delivered", "failed"]),
  count: z.number().int().min(1).max(10),
  purchases: z.array(packPurchaseSchema),
});

export type PackPurchaseBatch = z.infer<typeof packPurchaseBatchSchema>;

export const tierDisclosureRowSchema = z.object({
  tier: z.string(),
  chance: z.number(),
  faceCents: z.number().optional(),
  instantQuoteCents: z.number().optional(),
  income30dCents: z.number(),
  careerIncomeCents: z.number(),
  paybackDays: z.number(),
  bestExitX: z.number(),
});

export type TierDisclosureRow = z.infer<typeof tierDisclosureRowSchema>;

export const packSeriesSchema = z.object({
  series: z.array(
    z.object({
      code: z.string(),
      kind: z.string(),
      track: z.string(),
      trackName: z.string().optional(),
      priceCents: z.number(),
      horsesPerPack: z.number(),
      commitHash: z.string(),
      committedAt: z.string().nullable(),
      available: z.number(),
      tierProbabilities: z.record(z.string(), z.number()).optional(),
    }),
  ),
  tierDisclosure: z.array(tierDisclosureRowSchema).optional(),
  tierDisclosureBasis: z.string().optional(),
});

export const packHistorySchema = z.object({
  purchases: z.array(packPurchaseSchema),
  nextCursor: z.string().nullable(),
});

export const liveOpensSchema = z.object({
  opens: z.array(
    z.object({
      positionId: z.string().uuid(),
      at: z.string(),
      displayName: z.string(),
      series: z.string(),
      trackName: z.string().optional(),
      kind: z.string(),
      tiers: z.array(z.string()),
      topPull: z.string().nullable(),
      topHorse: packHorseSchema.nullable().optional(),
    }),
  ),
});

export const playerProfileSchema = z.object({
  playerId: z.number(),
  displayName: z.string(),
  memberSince: z.string(),
  currency: z.string(),
  country: z.string(),
  capabilities: capabilitySetSchema,
  stats: z.object({
    activeHorses: z.number(),
    retiredHorses: z.number(),
    totalRaces: z.number(),
    totalWins: z.number(),
    topThreeFinishes: z.number(),
    winRateBp: z.number(),
    totalEarningsCents: z.number(),
    sharesHeld: z.number(),
    openCoupons: z.number(),
    settledStakeCents: z.number(),
    settledReturnCents: z.number(),
    packSpendCents: z.number(),
  }),
  // Ownership PnL — exact completed-ledger sums (display-only; PLAN §Player
  // clarity contract). Optional for rollout skew.
  pnl: z
    .object({
      spentCents: z.number(),
      earnedCents: z.number(),
      cashedOutCents: z.number(),
      netCents: z.number(),
    })
    .optional(),
});

export type PlayerProfile = z.infer<typeof playerProfileSchema>;

export const wageringLeaderboardSchema = z.object({
  period: z.enum(["daily", "weekly", "monthly"]),
  startsAt: z.string(),
  endsAt: z.string(),
  rows: z.array(
    z.object({
      rank: z.number(),
      playerId: z.number(),
      displayName: z.string(),
      bets: z.number(),
      wageredCents: z.number(),
      returnedCents: z.number(),
      points: z.number(),
      isViewer: z.boolean().default(false),
    }),
  ),
});

export const ownershipLeaderboardSchema = z.object({
  period: z.enum(["weekly", "season"]),
  season: z.number(),
  poolCents: z.number(),
  totalPointsBp: z.number(),
  rows: z.array(
    z.object({
      rank: z.number(),
      horse: horseSummarySchema,
      owners: z.array(
        z.object({
          playerId: z.number(),
          displayName: z.string(),
          shareBp: z.number(),
          isViewer: z.boolean().default(false),
        }),
      ),
      points: z.number(),
      pointsBp: z.number(),
      projectedCents: z.number(),
      viewerProjectedCents: z.number(),
    }),
  ),
  viewer: z
    .object({
      bestRank: z.number().nullable(),
      totalPoints: z.number(),
      projectedCents: z.number(),
      sharesHeld: z.number(),
    })
    .nullable(),
});
